import { graphql, PageProps } from 'gatsby'
import React, { useState } from 'react'
import { QueryClient, QueryClientProvider } from 'react-query'

import { LayoutProvider } from '@application/Contexts/LayoutContext'
import { PageBuilder } from '@application/Interfaces/Features/PageBuilder/PageBuilder'
import { FreightCategoryType, PageNodeType, SingleTypeComponentType } from '@application/Types/ComponentTypes'
import { Locale } from '@application/Types/Locale'
import { StrapiTranslations, Translations } from '@application/Types/TranslationTypes'
import { STRAPI_COMPONENT_TYPES } from '@core/Constants/PageConstants'
import { Context } from '@infrastructure/Context'
import { isGdprCookieAccepted } from '@infrastructure/Cookies'
import { FREIGHT_GUIDE_TRANSLATION, HOME_TRANSLATION } from '@src/Core/Constants/TranslationConstants'
import { GlobalAppStyles } from '@src/GlobalAppStyles'
import { OrganizationProvider } from '@src/Infrastructure/Context/OrganizationContext'
import { FreightResourcesCollectionNodeType } from '@src/templates/types'
import { findBreadcrumbIndex, getBreadcrumbItems, getTranslations, modifiedHeroComponents } from '@src/templates/utils'
export { Head } from '@src/Application/Interfaces/Features/Seo'

const Template = (
    props: PageProps<
        { strapiPage: PageNodeType & { categories?: FreightCategoryType[] }; strapiTranslation: StrapiTranslations },
        {
            language: Locale
            globalFreightGuideCollectionNode?: FreightResourcesCollectionNodeType
            singleTypeComponents: SingleTypeComponentType
        }
    >
): JSX.Element => {
    const [sendMixpanelEvents, setSendMixpanelEvents] = useState(isGdprCookieAccepted('mixpanel'))
    const queryClient = new QueryClient({
        defaultOptions: {
            queries: {
                refetchOnMount: false,
                refetchOnWindowFocus: false,
                refetchOnReconnect: false,
            },
        },
    })

    const strapiTranslation = props.data.strapiTranslation

    const translations: Translations = getTranslations(strapiTranslation)

    const pageComponents = props.data.strapiPage.pageComponents ?? []
    const collectionNode = props.pageContext.globalFreightGuideCollectionNode
    const pageTitle = props.data.strapiPage.title
    const language = props.pageContext.language
    const homeTitle = translations[language]?.['home'] || HOME_TRANSLATION
    const collectionTitle = translations[language]?.['frightGuide'] || FREIGHT_GUIDE_TRANSLATION

    const breadcrumbComponent = {
        id: 'breadcrumb',
        strapi_component: STRAPI_COMPONENT_TYPES.BREADCRUMB,
        items: getBreadcrumbItems(
            language,
            collectionNode,
            pageTitle,
            homeTitle,
            collectionTitle,
            props.data.strapiPage.categories
        ),
    }

    const breadcrumbIndex = findBreadcrumbIndex(pageComponents)
    if (pageComponents[breadcrumbIndex]?.strapi_component !== STRAPI_COMPONENT_TYPES.BREADCRUMB) {
        pageComponents.splice(breadcrumbIndex, 0, breadcrumbComponent)
    }

    const singleTypeUSP = props.pageContext.singleTypeComponents['usp'][language]
    const singleTypeAgilitySection = props.pageContext.singleTypeComponents['agilitySection'][language]

    const modifiedPageComponents = modifiedHeroComponents({
        pageComponents,
        singleTypeUSP,
        singleTypeAgilitySection,
    })

    return (
        <Context.Provider
            value={{
                locale: language,
                translations,
                sendMixpanelEvents,
                setSendMixpanelEvents,
            }}
        >
            <OrganizationProvider>
                <QueryClientProvider client={queryClient}>
                    <LayoutProvider>
                        <GlobalAppStyles />
                        <PageBuilder
                            {...{
                                ...props,
                                data: {
                                    ...props.data,
                                    strapiPage: {
                                        ...props.data.strapiPage,
                                        pageComponents: modifiedPageComponents,
                                    },
                                },
                            }}
                        />
                    </LayoutProvider>
                </QueryClientProvider>
            </OrganizationProvider>
        </Context.Provider>
    )
}

export default Template

export const query = graphql`
    query ($id: String!) {
        strapiPage: strapiGlobalFreight(id: { eq: $id }) {
            ...StrapiGlobalFreightData
        }
        strapiTranslation {
            ...StrapiTranslations
        }
    }
`
